import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ReassignContextDialog } from '@/components/processes/ReassignContextDialog';
import { ReviewProcessStepSection } from '@/components/processes/ReviewProcessStepSection';
import { ProcessDetailsDialog } from '@/components/processes/ProcessDetailsDialog';
import { RecordingProcessMapDialog } from './dialogs/RecordingProcessMapDialog';
import {
  getSimplifiedInputEventTypes,
  lookupAssignee,
  Dialog,
  DialogShell,
  DialogShellHeader,
  DialogShellBody,
  DialogShellFooter,
  DecisionTableDialog,
  InputFormDialog,
  AssigneeDisplay,
  PROPOSED_ENTITY_CLASS,
  proposedEntityIcon,
  type StartEventContent,
  type ViewFetchState
} from '@bliks-io/ui';
import { UserPlus } from 'lucide-react';
import { CurrentStepSlotCard } from '@/components/processes/PreviousStepPreview';
import type { CreatedSuggestionSession } from '@/components/entity-pickers/hooks/useCreatedSuggestionSession';
import { useAssigneeLookup } from '@/lib/useAssigneeLookup';
import { useCanReadOrgCatalogs } from '@/hooks/useCanReadOrgCatalogs';
import {
  isUnclearStatus,
  pickReassigneeFromSuccessors,
  type ReassignPayload
} from '@/lib/successorEntityMapping';
import type {
  AssigneeModel,
  EntitySuggestion,
  ProcessEventNode,
  ProcessStepAssignmentStatus,
  ProcessStepRecording,
  ProcessStepRecordingDisplay,
  ProcessWithSteps,
  StepDecisionRule,
  StepInput
} from '@bliks-io/contracts';
import type { SuccessorUpdateData } from '../types';

interface StepRecordingDialogsProps {
  currentProcess?: ProcessWithSteps | null;
  processLoadState: ViewFetchState;
  currentRecording: ProcessStepRecording | null;
  entitySuggestions?: EntitySuggestion[];
  /**
   * Unified event nodes from `useProcess().events`. Used to recognise a
   * start-anchored recording; the map dialog fetches its own copy.
   */
  events?: ProcessEventNode[];
  currentRecordingStatus?: ProcessStepAssignmentStatus;
  completedProcessSteps: ProcessStepRecordingDisplay[];
  showProcessDetailsModal: boolean;
  setShowProcessDetailsModal: (v: boolean) => void;
  showAssignInsteadModal: boolean;
  setShowAssignInsteadModal: (v: boolean) => void;
  handleSuccessorsSubmit: (data: SuccessorUpdateData) => void;
  /** The flow's single created-suggestion ledger — handed to the context
   *  dialog so suggestions created there stay cancellable until the confirm
   *  below (one ledger, one withdraw site). */
  suggestionSession: CreatedSuggestionSession;
  showConfirmReassignDialog: boolean;
  pendingReassignData: ReassignPayload | null;
  handleCancelReassign: () => void;
  handleConfirmReassign: () => void;
  reassignPending: boolean;
  showInputModal: boolean;
  setShowInputModal: (v: boolean) => void;
  editingInputIndex: number | null;
  setEditingInputIndex: (v: number | null) => void;
  handleSaveInput: (data: StepInput | StartEventContent) => void;
  showDecisionTableModal: boolean;
  setShowDecisionTableModal: (v: boolean) => void;
  handleSaveDecisionRules: (rules: StepDecisionRule[]) => void;
  showProcessFlowModal: boolean;
  setShowProcessFlowModal: (v: boolean) => void;
}

export function StepRecordingDialogs(props: StepRecordingDialogsProps) {
  const { t } = useTranslation();
  const intermediateEventTypes = useMemo(() => getSimplifiedInputEventTypes(), []);

  const processId = props.currentRecording?.process;
  const recordingId = props.currentRecording?._id;
  const recording = props.currentRecording;
  // First steps anchor on their start event node since the issue-11 flip;
  // legacy 'START' rows (M2 orphan-repair stock) keep matching the literal.
  const isStartAnchoredRecording =
    recording?.previousStep === 'START' ||
    (typeof recording?.previousStep === 'string' &&
      (props.events ?? []).some(
        (event) => event.eventKind === 'start' && event._id === recording.previousStep
      ));

  // Every close affordance on the confirm dialog (esc, overlay, header X,
  // footer Cancel) must run the same cancel: it withdraws the flow's created
  // suggestions, drops the pending payload and reopens the assignment modal.
  // While the reassign POST is in flight the footer Cancel is disabled — gate
  // the other affordances identically so a withdraw can't race the ref write.
  const handleConfirmReassignClose = () => {
    if (props.reassignPending) return;
    props.handleCancelReassign();
  };

  return (
    <>
      <ProcessDetailsDialog
        open={props.showProcessDetailsModal}
        onOpenChange={props.setShowProcessDetailsModal}
        process={props.currentProcess || undefined}
        loadState={props.processLoadState}
      />

      <ReassignContextDialog
        open={props.showAssignInsteadModal}
        onOpenChange={(open) => !open && props.setShowAssignInsteadModal(false)}
        onSubmit={props.handleSuccessorsSubmit}
        // The recording flow has a second, destructive-warning confirm below —
        // the flow owns the session so suggestions created in this dialog stay
        // cancellable until THAT confirm (the dialog tracks into it instead of
        // keeping a ledger of its own).
        session={props.suggestionSession}
        title={t('reassignContext.title')}
        description={t('reassignContext.descriptionRecording')}
        contextBody={
          recording
            ? (staged: SuccessorUpdateData | null) => (
                <RecordingReassignContext
                  recording={recording}
                  staged={staged}
                  entitySuggestions={props.entitySuggestions}
                  onShowInMap={() => props.setShowProcessFlowModal(true)}
                />
              )
            : null
        }
        processId={processId}
        recordingId={recordingId}
        entitySuggestions={props.entitySuggestions}
        hideExternalContact={isStartAnchoredRecording}
      />

      <Dialog
        open={props.showConfirmReassignDialog}
        onOpenChange={(open) => !open && handleConfirmReassignClose()}
      >
        <DialogShell size="sm">
          <DialogShellHeader
            title={t('stepRecording.confirmReassign.title')}
            subtitle={t('stepRecording.confirmReassign.description')}
            onClose={handleConfirmReassignClose}
          />
          <DialogShellBody>
            <ConfirmReassigneePreview
              pending={props.pendingReassignData}
              entitySuggestions={props.entitySuggestions}
            />
          </DialogShellBody>
          <DialogShellFooter
            secondary={{
              label: t('common.cancel'),
              onClick: props.handleCancelReassign,
              disabled: props.reassignPending
            }}
            primary={{
              label: t('common.confirm'),
              onClick: props.handleConfirmReassign,
              loading: props.reassignPending
            }}
          />
        </DialogShell>
      </Dialog>

      <InputFormDialog
        open={props.showInputModal}
        onOpenChange={(open) => {
          props.setShowInputModal(open);
          if (!open) props.setEditingInputIndex(null);
        }}
        onSubmit={props.handleSaveInput}
        input={
          props.editingInputIndex !== null &&
          props.currentRecording?.inputs?.[props.editingInputIndex]
            ? props.currentRecording.inputs[props.editingInputIndex]
            : undefined
        }
        isSubmitting={false}
        processSteps={props.completedProcessSteps}
        eventTypes={intermediateEventTypes}
        existingInputs={props.currentRecording?.inputs}
        editingInputIndex={props.editingInputIndex}
      />

      {props.showDecisionTableModal && (
        <DecisionTableDialog
          onClose={() => props.setShowDecisionTableModal(false)}
          onSave={props.handleSaveDecisionRules}
          initialData={props.currentRecording?.rules || []}
        />
      )}

      {/* The page's one map, opened either from the bottom bar or from the
          reassign context's step preview. Sharing the instance is what makes
          that preview's button free: it already highlights this recording, and
          a second instance would mean a second layout request and a second set
          of query observers for the same diagram. */}
      <RecordingProcessMapDialog
        open={props.showProcessFlowModal}
        onOpenChange={props.setShowProcessFlowModal}
        currentRecording={props.currentRecording}
        currentRecordingStatus={props.currentRecordingStatus}
      />
    </>
  );
}

/**
 * Mirrors the assignment-screen preview: previous step → connector → assignee
 * card. The right card tracks the staged picker selection live — neutral
 * "choose assignee" placeholder before picking, entity card once an entity is
 * staged, amber unclear card when staged as unclear.
 */
function RecordingReassignContext({
  recording,
  staged,
  entitySuggestions,
  onShowInMap
}: {
  recording: ProcessStepRecording;
  staged: SuccessorUpdateData | null;
  entitySuggestions?: EntitySuggestion[];
  /** Opens the page's own map dialog — see the call site for why it is that
   *  one and not a second instance. */
  onShowInMap: () => void;
}) {
  const { t } = useTranslation();
  // The same catalog-read rule `useProcessMapDialogData` enforces for the
  // shared hosts, applied by hand: this button opens a map that is a SIBLING
  // component, so there is no `isReady` in scope to read it from. It decides
  // this preview's lookup and whether the map is offered.
  const canReadOrgCatalogs = useCanReadOrgCatalogs();
  const lookup = useAssigneeLookup({ enabled: canReadOrgCatalogs });
  const stagedUnclear = staged ? isUnclearStatus(staged) : false;
  const pair = staged && !stagedUnclear ? pickReassigneeFromSuccessors(staged) : null;
  // A suggestion pick has no real entity to resolve — render its proposed name
  // (blue) instead. Entity picks resolve against the org lookup as before.
  const newAssignee =
    pair?.type === 'entity'
      ? lookupAssignee({ type: 'entity', id: pair.assigneeId }, pair.assigneeModel, lookup)
      : undefined;

  // Right-side card: amber unclear is handled by the warning flag (slot stays
  // undefined); a proposed pick gets the blue proposed-name card; an
  // unresolved/empty pick gets the neutral "choose assignee" card so the
  // connector never dangles.
  const renderCurrentStepSlot = () => {
    if (stagedUnclear) return undefined;
    if (pair?.type === 'suggestion') {
      return (
        <ProposedAssigneeCard
          suggestionId={pair.assigneeId}
          model={pair.assigneeModel}
          entitySuggestions={entitySuggestions}
        />
      );
    }
    if (!newAssignee) return <ChooseAssigneeCard />;
    return undefined;
  };

  return (
    <ReviewProcessStepSection
      label={t('reassignContext.currentStep')}
      sourceProcessStep={recording.previousStep}
      assignedTo={newAssignee}
      assignedToModel={pair?.assigneeModel}
      showUnclearSuccessorWarning={stagedUnclear}
      currentStepSlot={renderCurrentStepSlot()}
      onShowInMap={canReadOrgCatalogs ? onShowInMap : undefined}
    />
  );
}

/** Right-side card for a staged *proposed* (suggestion) Team/System — its blue
 *  proposed name with the entity-type icon, mirroring the canvas placeholder. */
function ProposedAssigneeCard({
  suggestionId,
  model,
  entitySuggestions
}: {
  suggestionId: string;
  model: AssigneeModel;
  entitySuggestions?: EntitySuggestion[];
}) {
  const { t } = useTranslation();
  const lookup = useAssigneeLookup();
  const Icon = proposedEntityIcon(model === 'OrgFunction' ? 'orgFunction' : 'system');
  return (
    <CurrentStepSlotCard
      header={
        <div className={`flex items-center gap-2 min-w-[200px] ${PROPOSED_ENTITY_CLASS}`}>
          <Icon className="h-5 w-5 shrink-0" />
          <AssigneeDisplay
            assignedTo={{ type: 'suggestion', id: suggestionId }}
            assignedToModel={model}
            entitySuggestions={entitySuggestions}
            assigneeLookup={lookup}
            t={t}
          />
        </div>
      }
      stepLabel={t('processPage.YourProcessStep')}
    />
  );
}

/**
 * Confirm-dialog body: shows whom/what the recording is being reassigned to so
 * the user can review before confirming — a suggestion pick resolves to its
 * blue proposed name via `entitySuggestions`. The unclear path has no
 * assignee, so a short explanatory line stands in instead.
 */
function ConfirmReassigneePreview({
  pending,
  entitySuggestions
}: {
  pending: ReassignPayload | null;
  entitySuggestions?: EntitySuggestion[];
}) {
  const { t } = useTranslation();
  const lookup = useAssigneeLookup();

  if (!pending || pending.isUnclear) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('stepRecording.confirmReassign.unclearBody')}
      </p>
    );
  }

  if (!pending.reAssigneeId || !pending.reAssigneeModel) return null;

  return (
    <AssigneePreviewBox label={t('stepRecording.confirmReassign.assigneeLabel')}>
      <AssigneeDisplay
        assignedTo={{ type: pending.reAssigneeType, id: pending.reAssigneeId }}
        assignedToModel={pending.reAssigneeModel}
        entitySuggestions={entitySuggestions}
        assigneeLookup={lookup}
        t={t}
      />
    </AssigneePreviewBox>
  );
}

function AssigneePreviewBox({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">{label}</h3>
      <div className="rounded-md border bg-muted/30 px-3 py-2">{children}</div>
    </div>
  );
}

/** Right-side placeholder before an assignee is staged — neutral variant of
 *  the shared current-step card. */
function ChooseAssigneeCard() {
  const { t } = useTranslation();
  return (
    <CurrentStepSlotCard
      header={
        <div className="flex items-center gap-2 text-muted-foreground min-w-[200px]">
          <UserPlus className="h-5 w-5" />
          <span className="text-sm font-medium">{t('reassignContext.chooseAssignee')}</span>
        </div>
      }
      stepLabel={t('processPage.YourProcessStep')}
    />
  );
}
